import type { KBTemplate } from "@/lib/pharmacologyKnowledgeBase";
import { templateAliases, templateCategories } from "@/lib/library/libraryIndex";
import { formatRange } from "@/lib/units";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Activity,
  AlertTriangle,
  BookOpen,
  BrainCircuit,
  Clock3,
  FlaskConical,
  HeartPulse,
  Route,
  ShieldAlert,
  Star,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";

function titleCase(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function riskTone(level?: string) {
  const normalized = String(level ?? "").toLowerCase();
  if (/muito alto|critical|danger/.test(normalized)) return "border-destructive/50 text-destructive bg-destructive/5";
  if (/alto|high/.test(normalized)) return "border-orange-500/50 text-orange-500 bg-orange-500/5";
  if (/moderado|medium/.test(normalized)) return "border-yellow-500/50 text-yellow-500 bg-yellow-500/5";
  if (/baixo|low/.test(normalized)) return "border-emerald-500/40 text-emerald-500 bg-emerald-500/5";
  return "border-border text-muted-foreground";
}

function RiskItem({ label, level }: { label: string; level?: string }) {
  if (!level) return null;
  return (
    <div className="rounded-2xl border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <Badge variant="outline" className={`mt-2 ${riskTone(level)}`}>{level}</Badge>
    </div>
  );
}

function ArrayList({ values }: { values?: string[] }) {
  if (!values?.length) return <div className="text-sm text-muted-foreground">Sem dados estruturados.</div>;
  return (
    <ul className="space-y-1.5 text-sm text-muted-foreground">
      {values.map((value) => <li key={value} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /><span>{value}</span></li>)}
    </ul>
  );
}

function RecordList({ value }: { value?: Record<string, unknown> | string[] }) {
  if (!value) return <div className="text-sm text-muted-foreground">Sem dados estruturados.</div>;
  if (Array.isArray(value)) return <ArrayList values={value.map(String)} />;
  const entries = Object.entries(value).filter(([, item]) => item != null && item !== "");
  if (!entries.length) return <div className="text-sm text-muted-foreground">Sem dados estruturados.</div>;
  return (
    <div className="space-y-2">
      {entries.map(([key, item]) => (
        <div key={key} className="rounded-xl border bg-muted/20 p-2.5 text-sm">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{titleCase(key)}</div>
          <div className="mt-1 break-words">{typeof item === "string" || typeof item === "number" ? String(item) : JSON.stringify(item)}</div>
        </div>
      ))}
    </div>
  );
}

function PkMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border bg-muted/20 p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

export function LibraryEntityDetail({
  template,
  open,
  onOpenChange,
  favorite,
  onToggleFavorite,
}: {
  template: KBTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  favorite: boolean;
  onToggleFavorite: () => void;
}) {
  if (!template) return null;

  const aliases = templateAliases(template);
  const categories = templateCategories(template);
  const sourceRefs = Array.isArray(template.source_references)
    ? template.source_references.map(String)
    : template.source_references
      ? Object.entries(template.source_references).map(([key, value]) => `${key}: ${String(value)}`)
      : [];

  const pk = {
    onset: formatRange(template.onset_min_value, template.onset_max_value, template.onset_unit),
    comeup: formatRange(template.comeup_min_value, template.comeup_max_value, template.comeup_unit),
    peak: formatRange(template.peak_min_value, template.peak_max_value, template.peak_unit),
    duration: formatRange(template.total_duration_min_value, template.total_duration_max_value, template.total_duration_unit),
    halfLife: formatRange(template.half_life_min_value, template.half_life_max_value, template.half_life_unit),
    residual: formatRange(template.residual_min_value, template.residual_max_value, template.residual_unit),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[100vw] max-w-[100vw] h-[100dvh] max-h-[100dvh] rounded-none border-0 p-0 sm:w-[94vw] sm:max-w-5xl sm:h-auto sm:max-h-[92dvh] sm:rounded-2xl sm:border">
        <div className="flex h-full min-h-0 flex-col">
          <DialogHeader className="border-b px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))] sm:px-6 sm:pt-6">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <DialogTitle className="break-words text-2xl sm:text-3xl">{template.name}</DialogTitle>
                  <Badge variant="outline">{template.substance_type ?? "substância"}</Badge>
                  <Badge variant="secondary">{template.review_status ?? (template.needs_review ? "em revisão" : "revisão não informada")}</Badge>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {template.generic_name && template.generic_name !== template.name ? template.generic_name : template.pharmacological_class ?? template.clinical_category ?? "Registro farmacológico"}
                </div>
              </div>
              <Button
                type="button"
                size="icon"
                variant={favorite ? "default" : "outline"}
                onClick={onToggleFavorite}
                aria-label={favorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
              >
                <Star className={`h-4 w-4 ${favorite ? "fill-current" : ""}`} />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-2">
              {categories.slice(0, 5).map((category) => <Badge key={category} variant="outline" className="text-[10px]">{titleCase(category)}</Badge>)}
              {template.evidence_level ? <Badge variant="outline" className="text-[10px]">evidência: {template.evidence_level}</Badge> : null}
              {template.confidence ? <Badge variant="outline" className="text-[10px]">confiança: {template.confidence}</Badge> : null}
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-6">
            <Tabs defaultValue="essencial" className="space-y-4">
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-5">
                <TabsTrigger value="essencial">Essencial</TabsTrigger>
                <TabsTrigger value="farmacologia">Farmacologia</TabsTrigger>
                <TabsTrigger value="pkpd">PK/PD</TabsTrigger>
                <TabsTrigger value="seguranca">Segurança</TabsTrigger>
                <TabsTrigger value="fontes">Fontes</TabsTrigger>
              </TabsList>

              <TabsContent value="essencial" className="space-y-4">
                <Card className="rounded-3xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 font-medium"><BookOpen className="h-4 w-4 text-primary" />Resumo</div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                    {template.short_description ?? template.mechanism_summary ?? "Resumo ainda não estruturado para este registro."}
                  </p>
                </Card>

                {aliases.length > 0 && (
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Nomes, marcas e aliases</div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {aliases.slice(0, 24).map((alias) => <Badge key={alias} variant="secondary">{alias}</Badge>)}
                      {aliases.length > 24 ? <Badge variant="outline">+{aliases.length - 24}</Badge> : null}
                    </div>
                  </Card>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="flex items-center gap-2 text-sm font-medium"><BrainCircuit className="h-4 w-4 text-primary" />Classe e contexto</div>
                    <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                      <div><span className="text-foreground">Classe:</span> {template.pharmacological_class ?? "—"}</div>
                      <div><span className="text-foreground">Subclasse:</span> {template.pharmacological_subclass ?? "—"}</div>
                      <div><span className="text-foreground">Categoria clínica:</span> {template.clinical_category ?? "—"}</div>
                      <div><span className="text-foreground">Status regulatório:</span> {template.legal_status ?? "não informado"}</div>
                    </div>
                  </Card>
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="flex items-center gap-2 text-sm font-medium"><Route className="h-4 w-4 text-primary" />Vias e formulações</div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(template.available_routes ?? [template.default_route].filter(Boolean) as string[]).map((route) => <Badge key={route} variant="outline">{route}</Badge>)}
                      {(template.available_formulations ?? []).slice(0, 8).map((form) => <Badge key={form} variant="secondary">{form}</Badge>)}
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">Vias não são assumidas como equivalentes; PK/PD deve ser interpretada por formulação e contexto.</div>
                  </Card>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <PkMetric label="Início" value={pk.onset} />
                  <PkMetric label="Pico" value={pk.peak} />
                  <PkMetric label="Duração" value={pk.duration} />
                  <PkMetric label="Meia-vida" value={pk.halfLife} />
                  <PkMetric label="Residual" value={pk.residual} />
                  <PkMetric label="Modelo" value={template.default_curve_model ?? "—"} />
                </div>

                <div className="grid gap-2 sm:grid-cols-3">
                  <Button asChild variant="outline"><Link to="/interacoes"><Zap className="mr-2 h-4 w-4" />Ver interações</Link></Button>
                  <Button asChild variant="outline"><Link to="/laboratorio-pkpd"><FlaskConical className="mr-2 h-4 w-4" />Abrir PK/PD</Link></Button>
                  <Button asChild><Link to="/doses"><Activity className="mr-2 h-4 w-4" />Registros de dose</Link></Button>
                </div>
              </TabsContent>

              <TabsContent value="farmacologia" className="space-y-4">
                <Card className="rounded-3xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 font-medium"><FlaskConical className="h-4 w-4 text-primary" />Mecanismo</div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                    {template.mechanism_expanded ?? template.mechanism_summary ?? "Mecanismo ainda não estruturado."}
                  </p>
                </Card>
                <div className="grid gap-3 lg:grid-cols-2">
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Alvos primários</div>
                    <div className="mt-3"><RecordList value={template.primary_targets} /></div>
                  </Card>
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Alvos secundários</div>
                    <div className="mt-3"><RecordList value={template.secondary_targets} /></div>
                  </Card>
                </div>
                {template.receptor_profile?.length ? (
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Receptores</div>
                    <div className="mt-3 space-y-2">
                      {template.receptor_profile.map((row, index) => (
                        <div key={`${row.target}-${index}`} className="rounded-2xl border p-3 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2"><span className="font-medium">{row.target}</span>{row.confidence ? <Badge variant="outline">{row.confidence}</Badge> : null}</div>
                          <div className="mt-1 text-muted-foreground">{row.action_type}</div>
                          {row.clinical_relevance ? <div className="mt-1 text-xs">Relevância: {row.clinical_relevance}</div> : null}
                          {row.affinity_ki ? <div className="mt-1 text-xs text-muted-foreground">Afinidade informada: {row.affinity_ki}</div> : null}
                        </div>
                      ))}
                    </div>
                  </Card>
                ) : null}
                {template.transporter_profile?.length ? (
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Transportadores</div>
                    <div className="mt-3 space-y-2">
                      {template.transporter_profile.map((row, index) => (
                        <div key={`${row.target}-${index}`} className="rounded-2xl border p-3 text-sm">
                          <div className="font-medium">{row.target}</div>
                          <div className="mt-1 text-muted-foreground">{row.action_type}</div>
                          {row.clinical_relevance ? <div className="mt-1 text-xs">Relevância: {row.clinical_relevance}</div> : null}
                        </div>
                      ))}
                    </div>
                  </Card>
                ) : null}
                <Card className="rounded-3xl p-4 sm:p-5">
                  <div className="text-sm font-medium">Metabolismo e enzimas</div>
                  <div className="mt-3 grid gap-3 lg:grid-cols-2">
                    <RecordList value={template.enzyme_profile} />
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {(template.cyp_substrate ?? []).length ? <div><span className="text-foreground">Substrato CYP:</span> {template.cyp_substrate?.join(", ")}</div> : null}
                      {(template.cyp_inhibitor ?? []).length ? <div><span className="text-foreground">Inibidor CYP:</span> {template.cyp_inhibitor?.join(", ")}</div> : null}
                      {(template.cyp_inducer ?? []).length ? <div><span className="text-foreground">Indutor CYP:</span> {template.cyp_inducer?.join(", ")}</div> : null}
                      {(template.active_metabolites ?? []).length ? <div><span className="text-foreground">Metabólitos ativos:</span> {template.active_metabolites?.join(", ")}</div> : null}
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="pkpd" className="space-y-4">
                <Card className="rounded-3xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 font-medium"><Clock3 className="h-4 w-4 text-primary" />Linha temporal estruturada</div>
                  <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                    <PkMetric label="Início" value={pk.onset} />
                    <PkMetric label="Subida" value={pk.comeup} />
                    <PkMetric label="Pico" value={pk.peak} />
                    <PkMetric label="Duração" value={pk.duration} />
                    <PkMetric label="Meia-vida" value={pk.halfLife} />
                    <PkMetric label="Residual" value={pk.residual} />
                  </div>
                  <div className="mt-3 rounded-2xl border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">
                    Os tempos são faixas de referência e podem variar por via, formulação, alimento, metabolismo e contexto. Curvas ClinicalOS são modelos relativos de efeito/carga, não dosagens séricas medidas.
                  </div>
                </Card>
                <div className="grid gap-3 lg:grid-cols-2">
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Absorção e distribuição</div>
                    <div className="mt-3 space-y-3 text-sm text-muted-foreground">
                      <p>{template.absorption_notes ?? "Absorção ainda não estruturada."}</p>
                      <p>{template.distribution_notes ?? "Distribuição ainda não estruturada."}</p>
                      {template.bioavailability_min != null || template.bioavailability_max != null ? <div>Bioavailability registrada: {template.bioavailability_min ?? "—"}–{template.bioavailability_max ?? "—"}%</div> : null}
                      {template.protein_binding_percent != null ? <div>Ligação proteica registrada: {template.protein_binding_percent}%</div> : null}
                    </div>
                  </Card>
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Metabolismo e eliminação</div>
                    <div className="mt-3 space-y-3 text-sm text-muted-foreground">
                      <p>{template.metabolism_notes ?? "Metabolismo ainda não estruturado."}</p>
                      <p>{template.elimination_notes ?? "Eliminação ainda não estruturada."}</p>
                    </div>
                  </Card>
                </div>
                {(template.food_effect_notes || template.caffeine_interaction_notes || template.sleep_deprivation_risk_notes) ? (
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Modificadores de contexto</div>
                    <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                      {template.food_effect_notes ? <div><span className="text-foreground">Alimento:</span> {template.food_effect_notes}</div> : null}
                      {template.caffeine_interaction_notes ? <div><span className="text-foreground">Cafeína:</span> {template.caffeine_interaction_notes}</div> : null}
                      {template.sleep_deprivation_risk_notes ? <div><span className="text-foreground">Privação de sono:</span> {template.sleep_deprivation_risk_notes}</div> : null}
                    </div>
                  </Card>
                ) : null}
              </TabsContent>

              <TabsContent value="seguranca" className="space-y-4">
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                  <RiskItem label="Cardiovascular" level={template.cardiovascular_risk_level} />
                  <RiskItem label="Convulsão" level={template.seizure_risk_level} />
                  <RiskItem label="Psicose / mania" level={template.psychosis_mania_risk_level} />
                  <RiskItem label="Depressão respiratória" level={template.respiratory_depression_risk_level} />
                  <RiskItem label="Toxicidade serotoninérgica" level={template.serotonin_syndrome_risk_level} />
                  <RiskItem label="QT" level={template.qt_risk_level} />
                  <RiskItem label="Sedação" level={template.sedation_risk_level} />
                  <RiskItem label="Potencial de abuso" level={template.abuse_risk_level} />
                </div>
                {(template.major_warnings?.length || template.black_box_warnings?.length) ? (
                  <Card className="rounded-3xl border-destructive/30 bg-destructive/5 p-4 sm:p-5">
                    <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 text-destructive" />Alertas relevantes</div>
                    <div className="mt-3"><ArrayList values={[...(template.black_box_warnings ?? []), ...(template.major_warnings ?? [])]} /></div>
                  </Card>
                ) : null}
                <div className="grid gap-3 lg:grid-cols-2">
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Dependência e tolerância</div>
                    <div className="mt-3 space-y-3 text-sm text-muted-foreground">
                      <div><span className="text-foreground">Tolerância:</span> {typeof template.tolerance_profile === "string" ? template.tolerance_profile : template.tolerance_profile ? JSON.stringify(template.tolerance_profile) : "não estruturada"}</div>
                      <div><span className="text-foreground">Dependência:</span> {template.dependence_profile ? JSON.stringify(template.dependence_profile) : "não estruturada"}</div>
                    </div>
                  </Card>
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="text-sm font-medium">Abstinência / descontinuação</div>
                    <div className="mt-3 text-sm text-muted-foreground">
                      {typeof template.withdrawal_profile === "string" ? template.withdrawal_profile : template.withdrawal_profile ? JSON.stringify(template.withdrawal_profile) : "Dados ainda não estruturados."}
                    </div>
                  </Card>
                </div>
                {template.safety_notes ? (
                  <Card className="rounded-3xl p-4 sm:p-5">
                    <div className="flex items-center gap-2 font-medium"><ShieldAlert className="h-4 w-4 text-primary" />Notas de segurança</div>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{template.safety_notes}</p>
                  </Card>
                ) : null}
                {(template.common_adverse_effects?.length || template.serious_adverse_effects?.length) ? (
                  <div className="grid gap-3 lg:grid-cols-2">
                    <Card className="rounded-3xl p-4 sm:p-5"><div className="text-sm font-medium">Efeitos adversos comuns</div><div className="mt-3"><ArrayList values={template.common_adverse_effects} /></div></Card>
                    <Card className="rounded-3xl p-4 sm:p-5"><div className="text-sm font-medium">Efeitos adversos graves</div><div className="mt-3"><ArrayList values={template.serious_adverse_effects} /></div></Card>
                  </div>
                ) : null}
              </TabsContent>

              <TabsContent value="fontes" className="space-y-4">
                <Card className="rounded-3xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 font-medium"><BookOpen className="h-4 w-4 text-primary" />Rastreabilidade</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border p-3 text-sm"><div className="text-xs text-muted-foreground">Tipo de fonte</div><div className="mt-1">{template.source_type ?? "não informado"}</div></div>
                    <div className="rounded-2xl border p-3 text-sm"><div className="text-xs text-muted-foreground">Nível de evidência</div><div className="mt-1">{template.evidence_level ?? "não informado"}</div></div>
                    <div className="rounded-2xl border p-3 text-sm"><div className="text-xs text-muted-foreground">Status de revisão</div><div className="mt-1">{template.review_status ?? (template.needs_review ? "revisão necessária" : "não informado")}</div></div>
                    <div className="rounded-2xl border p-3 text-sm"><div className="text-xs text-muted-foreground">Confiança</div><div className="mt-1">{template.confidence ?? "não informada"}</div></div>
                  </div>
                  {template.source_notes ? <p className="mt-4 whitespace-pre-wrap text-sm text-muted-foreground">{template.source_notes}</p> : null}
                </Card>
                <Card className="rounded-3xl p-4 sm:p-5">
                  <div className="text-sm font-medium">Referências registradas</div>
                  <div className="mt-3"><ArrayList values={sourceRefs} /></div>
                </Card>
                <Card className="rounded-3xl border-warning/30 bg-warning/5 p-4 text-xs text-muted-foreground">
                  <div className="flex gap-2"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" /><span>Conteúdo estrutural ou em revisão não deve ser interpretado como validação clínica automática. Ausência de dados não significa ausência de risco.</span></div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="border-t px-4 py-3 pb-[calc(.75rem+env(safe-area-inset-bottom))] sm:px-6 sm:pb-3">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><HeartPulse className="h-3.5 w-3.5" />Biblioteca farmacológica ClinicalOS · conhecimento canônico separado do prontuário individual.</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
